/**
 * Graph Layout Utilities
 *
 * Helper functions for calculating concept graph layout positions,
 * building hierarchical structures, and finding learning paths.
 *
 * @module concept-graph/utils/graph-layout
 */

import type { Concept, ConceptRelation } from '../types';

export interface GraphNode {
  id: string;
  concept: Concept;
  x: number;
  y: number;
  depth: number; // Distance from root concepts
  categoryIndex: number;
  layerIndex: number; // For force simulation constraints
  targetY: number; // Target Y position for force simulation
}

export interface GraphEdge {
  source: string;
  target: string;
  type: 'requires' | 'enables' | 'relates_to';
  strength: number;
}

export interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

/**
 * Calculate depth from root for each concept
 * Root concepts have no prerequisites (depth = 0)
 * Each level of dependency increases depth by 1
 */
export function calculateConceptDepths(concepts: Concept[]): Map<string, number> {
  const depths = new Map<string, number>();
  const visited = new Set<string>();

  function calculateDepth(conceptId: string): number {
    if (depths.has(conceptId)) {
      return depths.get(conceptId)!;
    }

    if (visited.has(conceptId)) {
      // Circular dependency detected, treat as depth 0
      return 0;
    }

    visited.add(conceptId);

    const concept = concepts.find(c => c.id === conceptId);
    if (!concept) return 0;

    // If no prerequisites, this is a root concept
    const prereqs = concept.relationships?.requires || [];
    if (prereqs.length === 0) {
      depths.set(conceptId, 0);
      return 0;
    }

    // Depth is 1 + max depth of prerequisites
    const prereqDepths = prereqs.map(rel => {
      const prereqConcept = concepts.find(c => c.id === rel.targetConceptId);
      return prereqConcept ? calculateDepth(rel.targetConceptId) : 0;
    });

    const depth = prereqs.length > 0 ? Math.max(...prereqDepths) + 1 : 0;
    depths.set(conceptId, depth);
    visited.delete(conceptId);

    return depth;
  }

  // Calculate depth for all concepts
  concepts.forEach(concept => {
    if (!depths.has(concept.id)) {
      calculateDepth(concept.id);
    }
  });

  return depths;
}

/**
 * Get category color for visualization
 */
export function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    memory_management: '#3B82F6', // Blue
    control_flow: '#10B981',       // Green
    data_structures: '#8B5CF6',    // Purple
    functions: '#F59E0B',          // Orange
    types: '#EC4899',              // Pink
    concurrency: '#EF4444',        // Red
    io: '#14B8A6',                 // Teal
    error_handling: '#F97316',     // Dark Orange
    modules: '#6366F1',            // Indigo
    metaprogramming: '#A855F7'     // Violet
  };

  return colors[category] || '#64748B'; // Default gray
}

/**
 * Get category index for vertical positioning
 */
export function getCategoryIndex(category: string): number {
  const categories = [
    'memory_management',
    'control_flow',
    'data_structures',
    'functions',
    'types',
    'concurrency',
    'io',
    'error_handling',
    'modules',
    'metaprogramming',
    'design_patterns',
    'paradigms'
  ];

  const index = categories.indexOf(category);
  return index >= 0 ? index : 0;
}

/**
 * Get layer index for vertical positioning (learning progression)
 * Lower index = higher on screen (fundamentals at top)
 */
export function getLayerIndex(layer: string): number {
  const layers = [
    'fundamental',        // 0 - Core basics (top)
    'language_feature',   // 1 - Language-specific features
    'pattern',            // 2 - Design patterns
    'paradigm'            // 3 - Programming paradigms (bottom)
  ];

  const index = layers.indexOf(layer);
  return index >= 0 ? index : 0;
}

/**
 * Build graph data structure from concepts
 */
export function buildGraphData(concepts: Concept[]): GraphData {
  const depths = calculateConceptDepths(concepts);

  // Group concepts by layer and depth for better vertical distribution
  const layerGroups = new Map<string, Concept[]>();
  concepts.forEach(concept => {
    const key = `${concept.layer}-${depths.get(concept.id) || 0}`;
    if (!layerGroups.has(key)) {
      layerGroups.set(key, []);
    }
    layerGroups.get(key)!.push(concept);
  });

  // Sort concepts within each group by difficulty, then alphabetically
  layerGroups.forEach(group => {
    group.sort((a, b) => {
      if (a.difficulty !== b.difficulty) {
        return a.difficulty - b.difficulty;
      }
      return a.name.localeCompare(b.name);
    });
  });

  // Track concept index within each layer-depth group for vertical spacing
  const conceptIndices = new Map<string, number>();
  layerGroups.forEach(group => {
    group.forEach((concept, index) => {
      conceptIndices.set(concept.id, index);
    });
  });

  // Create nodes with layer-based positioning
  const nodes: GraphNode[] = concepts.map(concept => {
    const depth = depths.get(concept.id) || 0;
    const layerIndex = getLayerIndex(concept.layer);
    const categoryIndex = getCategoryIndex(concept.category);
    const conceptIndex = conceptIndices.get(concept.id) || 0;

    // Initial X position based on depth (will be refined by force simulation)
    const x = depth * 250 + 100; // 250px spacing between depth levels

    // Target Y position for layer (force simulation will constrain to this)
    // Layer centers: 200, 600, 1000, 1400
    const targetY = layerIndex * 400 + 200;

    // Initial Y with some variation
    const y = targetY + (concept.difficulty - 1) * 30 + conceptIndex * 70;

    return {
      id: concept.id,
      concept,
      x,
      y,
      depth,
      categoryIndex,
      layerIndex,
      targetY // Force simulation will pull nodes toward this Y
    };
  });

  // Create edges from relationships
  const edges: GraphEdge[] = [];

  concepts.forEach(concept => {
    const relationships = concept.relationships;

    // Add "requires" edges (prerequisites)
    relationships?.requires?.forEach(rel => {
      edges.push({
        source: rel.targetConceptId, // Prerequisite
        target: concept.id,           // Current concept
        type: 'requires',
        strength: rel.strength
      });
    });

    // Add "enables" edges (progression)
    relationships?.enables?.forEach(rel => {
      edges.push({
        source: concept.id,
        target: rel.targetConceptId,
        type: 'enables',
        strength: rel.strength
      });
    });

    // Add "relates to" edges
    relationships?.relatesTo?.forEach(rel => {
      edges.push({
        source: concept.id,
        target: rel.targetConceptId,
        type: 'relates_to',
        strength: rel.strength
      });
    });
  });

  return { nodes, edges };
}

/**
 * Find shortest learning path between two concepts using BFS
 */
export function findLearningPath(
  concepts: Concept[],
  startId: string,
  endId: string
): string[] | null {
  if (startId === endId) return [startId];

  // Build adjacency list (following "enables" and "requires" relationships)
  const adjacency = new Map<string, string[]>();

  concepts.forEach(concept => {
    adjacency.set(concept.id, []);

    // Can progress to concepts this enables
    concept.relationships?.enables?.forEach(rel => {
      adjacency.get(concept.id)!.push(rel.targetConceptId);
    });

    // Can progress from prerequisites to this concept
    concept.relationships?.requires?.forEach(rel => {
      if (!adjacency.has(rel.targetConceptId)) {
        adjacency.set(rel.targetConceptId, []);
      }
      adjacency.get(rel.targetConceptId)!.push(concept.id);
    });
  });

  // BFS to find shortest path
  const queue: Array<{ id: string; path: string[] }> = [{ id: startId, path: [startId] }];
  const visited = new Set<string>([startId]);

  while (queue.length > 0) {
    const { id, path } = queue.shift()!;

    if (id === endId) {
      return path;
    }

    const neighbors = adjacency.get(id) || [];
    for (const neighborId of neighbors) {
      if (!visited.has(neighborId)) {
        visited.add(neighborId);
        queue.push({
          id: neighborId,
          path: [...path, neighborId]
        });
      }
    }
  }

  return null; // No path found
}

/**
 * Get node radius based on difficulty
 */
export function getNodeRadius(difficulty: number): number {
  if (difficulty <= 2) return 30;
  if (difficulty === 3) return 40;
  return 50;
}

/**
 * Format concept name for display (truncate if too long)
 */
export function formatConceptName(name: string, maxLength: number = 20): string {
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength - 3) + '...';
}

/**
 * Filter graph data to show only local neighborhood (1-hop) around a concept
 * Shows: selected concept + prerequisites + enables + related concepts
 * This creates the "focused" view as specified in the product spec
 */
export function getLocalNeighborhood(
  graphData: GraphData,
  conceptId: string
): GraphData {
  // Get the concept IDs in the neighborhood
  const neighborIds = new Set<string>([conceptId]);

  // Find all edges connected to this concept
  graphData.edges.forEach(edge => {
    if (edge.source === conceptId) {
      neighborIds.add(edge.target);
    }
    if (edge.target === conceptId) {
      neighborIds.add(edge.source);
    }
  });

  // Filter nodes to only those in neighborhood
  const nodes = graphData.nodes.filter(node => neighborIds.has(node.id));

  // Filter edges to only those between neighborhood nodes
  const edges = graphData.edges.filter(edge =>
    neighborIds.has(edge.source) && neighborIds.has(edge.target)
  );

  return { nodes, edges };
}
